import { useAuth } from '@clerk/clerk-react';
import { useSWRConfig } from 'swr';
import { ApiError } from '@/lib/api';
import { useSpacesEnabled } from '@/lib/spaces/flag';
import type { SpaceDetail } from '@/types/spaces';

export function useSpaceWrite() {
  const { getToken, userId } = useAuth();
  const enabled = useSpacesEnabled();
  const { mutate } = useSWRConfig();
  const write = async <T,>(operation: (token: string) => Promise<T>): Promise<T> => {
    if (!enabled || !userId) throw new Error('Os espaços não estão disponíveis. Atualize a página.');
    const token = await getToken();
    if (!token) throw new Error('Sua sessão expirou. Entre novamente.');
    // Creation is not idempotent: a lost response must not automatically repeat a POST.
    try {
      return await operation(token);
    } catch (error) {
      // A definite 401 rejected the write before mutation; refresh once, never retry network failures.
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      const freshToken = await getToken({ skipCache: true });
      if (!freshToken) throw new Error('Sua sessão expirou. Entre novamente.');
      return operation(freshToken);
    }
  };
  const refresh = (key: unknown[]) => { void mutate(key).catch(() => undefined); };
  const remember = async (space: SpaceDetail) => {
    await mutate(['space', userId, space.id], space, { revalidate: false });
    refresh(['spaces', userId]);
  };
  return { write, refresh, remember, mutate, userId };
}
